import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ProjectInvitation } from "@/types/Invitation";

export function useProjectInvitations(projectId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch invitations for a project
  const { data: invitations = [], isLoading } = useQuery({
    queryKey: ["project-invitations", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_invitations")
        .select(`
          *,
          profiles!created_by (
            full_name,
            email
          )
        `)
        .eq("project_id", projectId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      return data.map((invitation: any) => ({
        ...invitation,
        expires_at: new Date(invitation.expires_at),
        created_at: new Date(invitation.created_at),
        updated_at: new Date(invitation.updated_at),
        inviter_name: invitation.profiles?.full_name || invitation.profiles?.email || "Unknown"
      })) as ProjectInvitation[];
    },
    enabled: !!projectId,
  });

  // Send invitation mutation
  const sendInvitationMutation = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: 'admin' | 'member' | 'viewer' }) => {
      const { data, error } = await supabase.functions.invoke('send-project-invitation', {
        body: { projectId, email, role }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-invitations", projectId] });
      toast({
        title: "Invitation sent",
        description: "Project invitation has been sent successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to send invitation",
        description: error.message || "An error occurred while sending the invitation.",
        variant: "destructive",
      });
    },
  });

  // Revoke invitation mutation
  const revokeInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase
        .from("project_invitations")
        .update({ status: "revoked" })
        .eq("id", invitationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-invitations", projectId] });
      toast({
        title: "Invitation revoked",
        description: "The invitation has been revoked successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to revoke invitation",
        description: error.message || "An error occurred while revoking the invitation.",
        variant: "destructive",
      });
    },
  });

  // Resend invitation mutation
  const resendInvitationMutation = useMutation({
    mutationFn: async (invitation: ProjectInvitation) => {
      // First revoke the old invitation
      await supabase
        .from("project_invitations")
        .update({ status: "revoked" })
        .eq("id", invitation.id);

      // Send a new invitation
      const { data, error } = await supabase.functions.invoke('send-project-invitation', {
        body: { 
          projectId, 
          email: invitation.email, 
          role: invitation.role 
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-invitations", projectId] });
      toast({
        title: "Invitation resent",
        description: "A new invitation has been sent successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to resend invitation",
        description: error.message || "An error occurred while resending the invitation.",
        variant: "destructive",
      });
    },
  });

  return {
    invitations,
    isLoading,
    sendInvitation: sendInvitationMutation.mutateAsync,
    revokeInvitation: revokeInvitationMutation.mutateAsync,
    resendInvitation: resendInvitationMutation.mutateAsync,
    isSending: sendInvitationMutation.isPending,
    isRevoking: revokeInvitationMutation.isPending,
    isResending: resendInvitationMutation.isPending,
  };
}

// Hook for accepting invitations
export function useInvitationAcceptance() {
  const { toast } = useToast();

  const acceptInvitationMutation = useMutation({
    mutationFn: async (token: string) => {
      const { data, error } = await supabase.functions.invoke('accept-project-invitation', {
        body: { token }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: "Invitation accepted",
        description: `You've joined "${data.projectName}" as a ${data.role}.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to accept invitation",
        description: error.message || "An error occurred while accepting the invitation.",
        variant: "destructive",
      });
    },
  });

  return {
    acceptInvitation: acceptInvitationMutation.mutateAsync,
    isAccepting: acceptInvitationMutation.isPending,
  };
}

// Hook to get invitation details by token (for invitation page)
export function useInvitationDetails(token: string) {
  return useQuery({
    queryKey: ["invitation-details", token],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_invitation_details', {
        invitation_token: token
      });

      if (error) throw error;
      
      if (!data || data.length === 0) {
        throw new Error("Invitation not found or expired");
      }

      return {
        ...data[0],
        expires_at: new Date(data[0].expires_at)
      };
    },
    enabled: !!token,
    retry: false,
  });
}